import { apiClient } from "./client";
import type { components } from "./schema";

/**
 * Typed client for ``/api/v1/desk/`` — the ticketing and execution layer.
 *
 * Shapes mirror the serializers in ``everythingcloud-backend/api/v1/desk/``.
 * Scope headers (``X-Context-Scope`` / ``X-Context-Id``) are injected by the
 * axios interceptor; the viewsets mount ``ScopeContextMixin`` and narrow rows
 * by containment, so a scope miss is a 404 rather than a 403.
 *
 * **Money is never a bare number here.** Every total arrives as a `Money`
 * carrying its currency, what could not be converted (per original currency,
 * deliberately not summed) and how many tickets had no figure at all. Rendering
 * `total` alone would quietly drop the gaps and produce a figure that looks
 * right — the whole reason the backend returns the extra fields.
 *
 * **`null` is not `0`.** A null saving means "we do not know what this is
 * worth"; zero would mean "this is worth nothing", and the two order a queue
 * very differently.
 */

// --- lifecycle --------------------------------------------------------------

/** The ten states. Mirrors ``apps/desk/lifecycle.py``. */
export type DeskStatus =
  | "detected"
  | "recommended"
  | "pending_approval"
  | "approved"
  | "remediated"
  | "verified"
  | "closed"
  | "blocked"
  | "dismissed"
  | "superseded";

/** Work that is still live. Mirrors ``lifecycle.ACTIVE``. */
export const ACTIVE_STATUSES: readonly DeskStatus[] = [
  "detected",
  "recommended",
  "pending_approval",
  "approved",
  "remediated",
  "blocked",
];

/** Delivered value. Mirrors ``lifecycle.REALIZED``. */
export const REALIZED_STATUSES: readonly DeskStatus[] = ["verified", "closed"];

export const STATUS_LABELS: Record<DeskStatus, string> = {
  detected: "Detected",
  recommended: "Recommended",
  pending_approval: "Pending approval",
  approved: "Approved",
  remediated: "Remediated",
  verified: "Verified",
  closed: "Closed",
  blocked: "Blocked",
  dismissed: "Dismissed",
  superseded: "Superseded",
};

// --- money ------------------------------------------------------------------

/**
 * A converted total with its provenance.
 *
 * `unconverted` is a mapping from original currency to amount and is **never
 * summed**: amounts are in it precisely because there is no rate to express
 * them in one unit. 50 JPY and 30 CHF are not 80 of anything.
 */
export interface Money {
  total: string;
  currency: string;
  /** Per original currency. Render as-is or count; never add up. */
  unconverted: Record<string, string>;
  /** Tickets per unconverted currency, so a gap can say how many rows it hides. */
  unconverted_items: Record<string, number>;
  /** How much of `total` came via a carried-forward rate. */
  carried: string;
  /** Tickets with no figure at all. Distinct from `unconverted`. */
  unvalued_count: number;
  /**
   * Only on a figure at the customer's price: tickets with a figure that could
   * not be priced, because they have no credential or their customer has no
   * resale rate on file. Left out of the total, never added at the billed figure.
   */
  unpriced_count?: number;
}

/** True when a total is hiding something a reader should be told about. */
export function hasGaps(money: Money): boolean {
  return (
    Object.keys(money.unconverted).length > 0 ||
    Object.keys(money.unconverted_items ?? {}).length > 0 ||
    money.unvalued_count > 0 ||
    (money.unpriced_count ?? 0) > 0
  );
}

/**
 * Why a ticket has, or has not, a figure at the customer's price.
 *
 * `rate`: the customer's resale rate priced it. `no_rate`: the customer has no
 * rate on file, so the price is unknown, not the billed figure. `no_credential`:
 * a manually raised ticket with no account, so there is no customer to price
 * for.
 */
export type CustomerPriceSource = "rate" | "no_rate" | "no_credential";

// --- tickets ------------------------------------------------------------------

/**
 * Whose hourly rate priced a ticket.
 *
 * `ticket` is a rate somebody put on this ticket (or approval fixed onto it);
 * `organization` is the rate resolved up the tree; null is no rate anywhere,
 * which is an absence and is never rendered as a zero.
 */
export type LabourRateSource = "ticket" | "organization" | null;

/** Mirrors ``DeskTicketListSerializer`` exactly. Deliberately narrow: a queue of
 *  fifty rows should not carry fifty rows' worth of detail over the wire. */
export interface DeskTicketListRow {
  id: string;
  reference: number | null;
  title: string;
  status: DeskStatus;
  is_open: boolean;
  source_key: string;
  rule_id: string;
  resource_ref: string;
  provider: string;
  category: string;
  priority: string;
  risk_level: string;
  reversible: boolean | null;
  organization: string;
  organization_name: string;
  assigned_org: string | null;
  assigned_team: string | null;
  assigned_team_name: string | null;
  assigned_user: number | null;
  assigned_user_email: string | null;
  /** Null means unknown, never zero. */
  estimated_saving_low: string | null;
  estimated_saving_high: string | null;
  currency: string;
  realized_saving: string | null;
  /** A typed figure is not the same claim as a measured one. */
  realized_saving_source: string;
  /**
   * What we expect the work to cost: effort in hours × the effective hourly
   * rate, computed by the server. Null when either is missing — not zero.
   */
  implementation_cost_estimate: string | null;
  /** The estimate's own currency, which need not be the ticket's. "" when none. */
  implementation_cost_estimate_currency: string;
  implementation_cost_estimate_source: LabourRateSource;
  /** The per-hour rate in force for this ticket: the ticket's own, else the
   *  organization's. Null means none is set anywhere in the chain. */
  labour_rate: string | null;
  labour_rate_currency: string;
  labour_rate_source: LabourRateSource;
  /**
   * The same savings at the price the customer pays: the billed figure carried
   * through the customer's resale rate on the day it was derived. In the
   * ticket's own currency. Null with a reason in `customer_price_source`.
   */
  customer_saving_low: string | null;
  customer_saving_high: string | null;
  customer_realized_saving: string | null;
  /** The signed percentage of the rate that priced it; null when none did. */
  customer_rate_percent: string | null;
  customer_price_source: CustomerPriceSource;
  verification_status: string;
  verification_scope: string;
  recurrence_count: number;
  composition_source: string;
  created: string;
  modified: string;
  status_changed_at: string | null;
  /** The clock running right now; "" when no promise is being measured. */
  sla_kind: SlaKind | "";
  sla_due_at: string | null;
  sla_paused_at: string | null;
  sla_breached_at: string | null;
  /** Verdict of the last clock that closed; null when none ever ran. */
  sla_met: boolean | null;
}

export type SlaKind = "first_response" | "approval" | "remediation";
export const SLA_KIND_LABELS: Record<SlaKind, string> = {
  first_response: "First response",
  approval: "Approval",
  remediation: "Remediation",
};

/**
 * Where a ticket may go next, for the current user, and what the move needs.
 *
 * Computed server-side on purpose. Deriving it here would mean a second copy of
 * the transition table and the role ceiling in TypeScript, and two copies of a
 * rule this load-bearing drift — showing up as a button that 403s.
 */
export interface AllowedTransition {
  status: DeskStatus;
  label: string;
  /** The Desk permission this move requires. */
  action: string;
  /** Entry-gate fields the server will insist on. */
  requires: string[];
}

/** A schedule linked to a ticket, as the ticket's detail names it. */
export type DeskTicketSchedule =
  components["schemas"]["DeskTicketDetail"]["schedules"][number];

/** Mirrors ``DeskTicketDetailSerializer``, which extends the list serializer. */
export interface DeskTicketDetail extends DeskTicketListRow {
  allowed_transitions: AllowedTransition[];
  /**
   * Whether this caller may move responsibility.
   *
   * Server-computed alongside `allowed_transitions`, and separate from them:
   * transfer is independent of the lifecycle, so a closed ticket can still change
   * hands.
   */
  can_transfer: boolean;
  detail: string;
  recommendation: string;
  notes: string;
  estimated_saving_text: string;
  estimated_effort_hours: number | null;
  /** Hours it actually took. Recorded on the way to verified, or by hand. */
  actual_effort_hours: number | null;
  estimated_implementation_cost: string | null;
  /**
   * This ticket's own hourly rate, when a person priced this job differently
   * from the organization or approval fixed the organization's rate onto it.
   * Writable until the ticket is verified or closed; null defers to the
   * organization's live rate.
   */
  rate_snapshot: string | null;
  rate_currency_snapshot: string;
  actual_implementation_cost: string | null;
  automation_eligible: boolean | null;
  baseline_cost: string | null;
  baseline_window_start: string | null;
  baseline_window_end: string | null;
  applied_at: string | null;
  applied_by: number | null;
  applied_action: string;
  observed_cost: string | null;
  observation_window_start: string | null;
  observation_window_end: string | null;
  blocked_on_org: string | null;
  blocked_reason: string;
  dismissed_reason: string;
  owner_type: string;
  credential: string | null;
  credential_nickname: string | null;
  inventory_item: number | null;
  run: string | null;
  /** When a run last touched this ticket. Shown to a company in place of runs. */
  last_analysed_at: string | null;
  assigned_org_name: string | null;
  /** Consecutive runs that did not report this finding. Two supersedes it. */
  absent_runs: number;
  remediation_requested: boolean;
  remediation_urgency: string;
  remediation_notes: string;
  campaign: string | null;
  campaign_name: string | null;
  /** The ticket this one was folded into, when it was a duplicate. */
  merged_into: { id: string; reference: number | null; title: string } | null;
  /** How many duplicates were folded into this one. */
  merged_from_count: number;
  /** Count and whether the caller follows it; the list is on its own route. */
  watchers: { count: number; watching: boolean };
  resolved_at: string | null;
  /**
   * The Scheduling schedules this ticket proposed or was linked to, those the
   * reader may see in Scheduling; empty otherwise.
   */
  schedules: DeskTicketSchedule[];
  evidence: EvidenceRecord[];
  assignments: AssignmentEntry[];
  comments: DeskComment[];
}

export interface FacetValue {
  /** What to send back as the filter. */
  value: string;
  /** What to show. Equals `value` unless the value is an id. */
  label: string;
  count: number;
}

export type DeskFacets = Record<string, FacetValue[]>;

/** Someone who may be put on a ticket. A picker, not a user representation. */
export interface AssignableUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

/**
 * What a person supplies to raise a ticket.
 *
 * `source_key` and `owner_type` are deliberately absent: the server stamps
 * both, so a client cannot raise a ticket claiming to have come from a run.
 *
 * Every money field is optional and must stay unset rather than defaulting to
 * zero — "we do not know what this is worth" and "this is worth nothing" order
 * a queue very differently.
 */
export interface RaiseItemPayload {
  organization: string;
  title: string;
  detail?: string;
  recommendation?: string;
  category?: string;
  priority?: string;
  risk_level?: string;
  reversible?: boolean;
  estimated_saving_low?: string;
  estimated_saving_high?: string;
  currency?: string;
  estimated_effort_hours?: number;
  resource_ref?: string;
}

/** One row of the level-below rollup. What it lists depends on who is asking:
 *  a distributor reads MSPs, an MSP reads companies, a company reads itself. */
export interface ChildOrgRollup {
  organization: string | null;
  organization_name: string | null;
  total: number;
  open: number;
  realized: number;
  /** Open, with nobody on it. */
  unassigned: number;
  /** Open, and older than the queue's own ageing threshold. */
  ageing: number;
  awaiting_approval: number;
  predicted_saving: Money;
  /** The same, at what the customers pay. Carries `unpriced_count`. */
  predicted_saving_at_customer_price: Money;
  realized_saving: Money;
}

/**
 * One group in a grouped queue.
 *
 * Counted over the whole filtered set, not the page — a header says 40 on page
 * one rather than the 12 rows that happen to fit.
 *
 * `totals` is per currency and deliberately unconverted. A group header is a
 * glance; a converted figure there would need the same unconverted and unvalued
 * caveats the KPI strip carries, at which point it is no longer a glance.
 */
export interface DeskGroup {
  value: string;
  label: string;
  count: number;
  totals: Record<string, string>;
}

/**
 * Axes the queue can be grouped by.
 *
 * `organization` is why this exists. In Plane an issue belongs to a fixed
 * project; a Desk ticket's owner **changes** — transfer moves it up or down its
 * own chain — so grouping by client is how an MSP reads the portfolio it bills,
 * not one option among many.
 */
export const GROUP_BY_OPTIONS = [
  { key: "", label: "None" },
  { key: "organization", label: "Company" },
  { key: "status", label: "Status" },
  { key: "priority", label: "Priority" },
  { key: "source_key", label: "Source" },
  { key: "assigned_team", label: "Team" },
] as const;

export type DeskGroupBy = (typeof GROUP_BY_OPTIONS)[number]["key"];

export interface DeskTicketPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: DeskTicketListRow[];
  /** Counts over the *filtered* set, so selecting one facet narrows the rest. */
  facets: DeskFacets;
  /** Every status including the zeros, so tabs do not change shape. */
  status_counts: Record<DeskStatus, number>;
  /** Present only when `group_by` was sent. */
  groups?: DeskGroup[];
}

export interface DeskTicketFilters {
  status?: string;
  source_key?: string;
  rule_id?: string;
  priority?: string;
  risk_level?: string;
  verification_status?: string;
  organization?: string;
  team?: string;
  assignee?: string;
  campaign?: string;
  /** Tickets a given run created or updated. */
  run?: string;
  /** Billed currency, for the gap indicator's "show them" link. */
  billed_currency?: string;
  /** Responsibility: whose organization the work is with. */
  assigned_org?: string;
  /** On me, or on a team I am in. */
  mine?: "true";
  /** Untouched for this many days. */
  stale_days?: number;
  unassigned?: "true";
  open?: "true";
  /** Only 7, 14 or 30 — anything else is a 400, not an empty page. */
  age?: "7" | "14" | "30";
  q?: string;
  page?: number;
  page_size?: number;
  /**
   * Facet counts are per filter set, not per page — paging changes none of
   * them. Send `false` when only the page number moved and the client already
   * holds the counts: it saves nine aggregate scans of the whole filtered
   * queryset per request.
   */
  facets?: "false";
  /** Groups the queue. Ordering plus headers, never a nested response. */
  group_by?: string;
  /** Server-side sort: `created`, `reference`, `priority`, `status`, `-` prefix for descending. */
  ordering?: string;
}

// --- analytics --------------------------------------------------------------

export interface QueueSummary {
  currency: string;
  open: number;
  /** The product's own stated failure mode. Shown, not filtered to. */
  unassigned: number;
  blocked: number;
  recurring: number;
  predicted_saving: Money;
  /**
   * What delivering the open queue is predicted to cost. Optional: a backend
   * that does not compute it omits it, and it is **never** netted off the
   * saving — one is a promise about the bill, the other a cost of doing the
   * work, and a single "net" figure would hide which of the two moved.
   */
  predicted_implementation_cost?: Money;
  /** Measured and typed are two different claims and are never added. */
  realized_measured: Money;
  realized_typed: Money;
  /**
   * The predicted and measured figures at the price the customers pay: what an
   * MSP demonstrates it saved its clients. Each carries `unpriced_count`.
   */
  predicted_saving_at_customer_price: Money;
  realized_measured_at_customer_price: Money;
  ageing: { over_7d: number; over_14d: number; over_30d: number };
  /** Every status including the zeros, over the whole visible queue — not the
   *  filtered set, so a rail count cannot argue with the table beside it. */
  status_counts: Record<DeskStatus, number>;
}

export interface SourceScorecard {
  source_key: string;
  total: number;
  open: number;
  realized: number;
  dismissed: number;
  superseded: number;
  /** Null when nothing has been decided yet — never 0. */
  dismissal_rate: number | null;
  predicted_saving: Money;
  realized_saving: Money;
}

export interface RealisedSummary {
  currency: string;
  count: number;
  predicted: Money;
  realized: Money;
  measured: Money;
  typed: Money;
  measured_count: number;
  typed_count: number;
  /** Null when either side has gaps — the ratio would be a fiction. */
  accuracy: string | null;
}

// --- evidence and history ---------------------------------------------------

export interface EvidenceLink {
  id: string;
  /** What kind of row this points at: `inventory_item`, `cost_record`, … */
  kind: string;
  /** The row's primary key, as text. Stable across renames; not for display. */
  object_id: string;
  role: "primary" | "supporting" | "baseline";
  /** What to show. Empty when the row has since been deleted. */
  label: string;
  /** Context under the name — type, region, state, or a date. */
  sublabel: string;
  /** Whether a page exists to open for this row. */
  linkable: boolean;
}

export interface EvidenceRecord {
  id: string;
  created: string;
  run: string | null;
  source_key: string;
  template_version: string;
  window_start: string | null;
  window_end: string | null;
  model_name: string;
  prompt_version: string;
  confidence_score: number | null;
  /** The finding as the source recorded it. Present on newer API responses. */
  raw_finding?: Record<string, unknown> | null;
  /** Not on the wire: the API sends the derivation as links, not a blob. */
  selector?: Record<string, unknown> | null;
  payload_digest?: string;
  selector_row_count: number | null;
  links: EvidenceLink[];
}

export interface AuditEntry {
  id: string;
  created: string;
  action: string;
  actor: number | null;
  actor_email: string | null;
  /** True when the platform acted on someone else's ticket. Never silent. */
  is_intervention: boolean;
  from_value: Record<string, unknown> | null;
  to_value: Record<string, unknown> | null;
  reason: string;
}

export interface AssignmentEntry {
  id: string;
  created: string;
  from_org_name: string | null;
  to_org_name: string | null;
  from_team_name: string | null;
  to_team_name: string | null;
  reason: string;
  actor_email: string | null;
}

export interface DeskHistory {
  audit: AuditEntry[];
  assignments: AssignmentEntry[];
}

// --- teams, runs, sources ---------------------------------------------------

export interface TeamMember {
  id: string;
  email: string;
  full_name: string;
}

export interface DeskTeam {
  id: string;
  name: string;
  description: string;
  organization: string;
  organization_name: string;
  lead: string | null;
  lead_email: string;
  member_count: number;
  members: TeamMember[];
  is_active: boolean;
  created: string;
}

export type DeskRunStatus =
  | "running"
  | "completed"
  | "failed"
  | "skipped"
  | "aborted"
  | "cancelled";

export interface DeskRun {
  id: string;
  organization: string;
  organization_name: string;
  credential: number | null;
  credential_nickname: string;
  provider: string;
  source_key: string;
  source_label: string;
  status: DeskRunStatus;
  window_start: string | null;
  window_end: string | null;
  is_first_run: boolean;
  model_name: string;
  input_tokens: number | null;
  output_tokens: number | null;
  run_cost: string | null;
  run_cost_currency: string;
  tickets_created: number;
  tickets_updated: number;
  /** Why reconciliation was held back, when it was. Blank when it ran. */
  skip_reason: string;
  error: string;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  created: string;
}

export interface DeskSource {
  key: string;
  label: string;
  detection: "rules" | "ai" | "external" | "human";
  /** What the source is about, for grouping. */
  category: "cost" | "resources" | "recommendations" | "governance" | "manual";
  category_label: string;
  /** Whether it runs on its own. A by-hand source cannot be scheduled or run. */
  automated: boolean;
  default_window_days: number;
}

export type DeskAction =
  | "view"
  | "create"
  | "edit"
  | "delete"
  | "export"
  | "comment"
  | "assign"
  | "transfer"
  | "approve"
  | "resolve"
  | "dismiss"
  | "run_analysis";

/** What the caller may do, and where they sit. Ceiling ∩ groups, from the server. */
/** The Desk permission vocabulary the group editor renders from. */
export interface DeskPermissionsCatalog {
  actions: string[];
  /** Named bundles that write the checkbox matrix; the stored value is always the matrix. */
  presets: Record<string, string[]>;
  /** What each role may ever be granted. A checkbox outside it never takes effect. */
  ceilings: Record<string, string[]>;
  /** Verbs that gate no route today. */
  inert: string[];
}

export interface DeskMe {
  actions: DeskAction[];
  level: "platform" | "distributor" | "msp" | "company";
  organization: string | null;
  organization_name: string;
  /**
   * Whose escalations the reader works: their own organization, or for
   * platform staff the platform operator's, where an escalation past the
   * distributor lands. Optional so a server that predates it still reads.
   */
  escalation_organization?: string | null;
  escalation_organization_name?: string;
}

export interface SourceReadiness {
  credential: number;
  credential_nickname: string;
  provider: string;
  organization: string;
  organization_name: string;
  ready: boolean;
  reason: string;
  available_on: string | null;
  earliest_data: string | null;
  last_run: {
    id: string;
    status: DeskRunStatus;
    finished_at: string | null;
    tickets_created: number;
    tickets_updated: number;
  } | null;
  /** Null when the schedule will not run this account: unscheduled, or paused. */
  next_run: string | null;
  /** Scheduled runs of this source are paused for the account's company. */
  schedule_paused: boolean;
}

export interface RunNowPayload {
  source_key: string;
  credential?: number;
  organization?: string;
}

export interface RunDispatch {
  dispatched: Array<{ source_key: string; credential: number }>;
  /** Accounts in this run whose scheduled runs are paused. The run still goes ahead. */
  paused_credentials: number[];
  /** Says so in a sentence when any are; blank otherwise. */
  note: string;
}

export interface ThroughputRow {
  organization: string | null;
  organization_name: string | null;
  period: string | null;
  raised: number;
  worked: number;
  closed: number;
}

export interface OwnerClassRow {
  owner_type: string;
  total: number;
  realized: number;
  measured_count: number;
  typed_count: number;
  predicted_saving: Money;
  realized_saving: Money;
}

export type AIEnabled = "on" | "off" | "inherit";

export interface DeskWatcher {
  id: string;
  user: string | null;
  user_email: string | null;
  team: string | null;
  team_name: string | null;
  created: string;
}

export interface DeskCampaign {
  id: string;
  organization: string;
  organization_name: string;
  name: string;
  rule_id: string;
  is_active: boolean;
  created_by: string | null;
  created_by_email: string | null;
  created: string;
  /** Over the tickets the caller can see, so two callers may read two totals. */
  counts: { open: number; done: number };
  money: { predicted: Money; realised: Money };
}

export interface CampaignPayload {
  organization: string;
  name: string;
  rule_id?: string;
  is_active?: boolean;
}

export type BulkAction =
  | "assign"
  | "transfer"
  | "priority"
  | "campaign"
  | "transition";

export interface BulkRequest {
  ids: string[];
  action: BulkAction;
  /** Shaped per action: assign {team,user}; transfer {to_org,reason};
   *  priority {priority}; campaign {campaign}; transition {target,...gates}. */
  params: Record<string, unknown>;
}

export interface BulkResponse {
  done: string[];
  refused: { id: string; reason: string }[];
}

export interface SlaClockCounts {
  met: number;
  breached: number;
  running: number;
  at_risk: number;
  overdue: number;
}

export interface SlaSummary {
  met: number;
  breached: number;
  running: number;
  paused: number;
  at_risk: number;
  overdue: number;
  by_clock: Record<SlaKind, SlaClockCounts>;
}

export interface SlaOwn {
  sla_first_response_hours: number | null;
  sla_approval_hours: number | null;
  sla_remediation_hours: number | null;
  sla_business_hours_only: boolean | null;
}

export interface SlaConfig {
  organization: string | null;
  organization_name: string;
  /** Null is inherit, on every field. */
  own: SlaOwn;
  effective: {
    first_response_hours: number | null;
    approval_hours: number | null;
    remediation_hours: number | null;
    business_hours_only: boolean;
    /** Field name to the organization that supplied it, null for none. */
    sources: Record<keyof SlaOwn, string | null>;
    source_names: Record<keyof SlaOwn, string>;
  };
  can_edit: boolean;
}

export type SlaConfigUpdate = Partial<SlaOwn>;

/**
 * What an hour of this organization's work costs.
 *
 * Resolved exactly the way the SLA promises are — nearest ancestor first, null
 * meaning inherit — because an MSP sets one rate and every company under it
 * should cost the same to serve unless somebody says otherwise. With no rate
 * anywhere in the chain a ticket has hours and no derived cost, which is an
 * absence the ticket says out loud rather than a zero.
 */
export interface LabourOwn {
  /** Per hour. Null is inherit, never "free". */
  labour_rate: string | null;
  /** "" is inherit, for the same reason null is on the rate. */
  labour_rate_currency: string;
}

export interface LabourConfig {
  organization: string | null;
  organization_name: string;
  own: LabourOwn;
  effective: {
    labour_rate: string | null;
    labour_rate_currency: string;
    /** Field name to the organization that supplied it, null for none. */
    sources: Record<keyof LabourOwn, string | null>;
    source_names: Record<keyof LabourOwn, string>;
  };
  can_edit: boolean;
}

export type LabourConfigUpdate = Partial<LabourOwn>;

/**
 * One pause the schedule page is about. `own` sits on the organization itself,
 * `above` on an ancestor (and governs it), `below` on something it manages.
 */
export interface SourcePause {
  organization: string;
  organization_name: string;
  relation: "own" | "above" | "below";
  paused_by: string | null;
  paused_by_name: string;
  paused_at: string;
  reason: string;
  /** The organization, or "The platform", that wrote the pause. */
  set_by_name: string;
  /** The caller reaches its organization and nobody above them wrote it. */
  can_resume: boolean;
}

export interface SourceSchedule {
  key: string;
  label: string;
  /** Whether beat runs this source at all. */
  scheduled: boolean;
  /** Null when unscheduled or paused for this organization. */
  next_run: string | null;
  /** Paused here, by this organization's own pause or one above it. */
  paused: boolean;
  /** Own first, then above, then below. */
  pauses: SourcePause[];
}

/** Null organization: the platform view, which lists every pause in reach and pauses nothing itself. */
export interface ScheduleConfig {
  organization: string | null;
  organization_name: string;
  can_edit: boolean;
  sources: SourceSchedule[];
}

export interface ScheduleUpdate {
  source_key: string;
  paused: boolean;
  reason?: string;
}

/** The AI settings that are caps rather than preferences. */
export type AILimitField =
  | "ai_enabled"
  | "ai_allow_platform_key"
  | "ai_monthly_quota_usd";

export interface AIConfig {
  /** Null: the platform's own defaults, for unanchored staff with no organization picked. */
  organization: string | null;
  organization_name: string;
  /** distributor, msp or company; blank for the platform view. The quota is per company. */
  organization_type: string;
  /** Each limit on this organization's own row that somebody else set, by name. */
  own_set_by: Partial<Record<AILimitField, string>>;
  /** The limits on its own row the caller may narrow and not lift. */
  own_locked: AILimitField[];
  own: {
    ai_enabled: AIEnabled;
    ai_credential: number | null;
    ai_allow_platform_key: boolean | null;
    ai_model: string;
    ai_monthly_quota_usd: string | null;
    /** Null means inherit; 0 is a real setting, not an absence. */
    ai_temperature: number | null;
  };
  effective: {
    enabled: boolean;
    enabled_source: string;
    credential: number | null;
    credential_nickname: string;
    key_owner: string | null;
    key_owner_name: string;
    allow_platform_key: boolean;
    provider: string;
    model: string;
    /** A ceiling for each company, not for a branch together. */
    quota_usd: string;
    /** Who set the allowance that binds: "The platform" or an organization. */
    quota_source_name: string;
    /** Why the quota stored on this organization's own row does not apply, or "". */
    quota_note: string;
    /** Null means the provider's own default. */
    temperature: number | null;
    resolves: boolean;
    reason: string;
  };
  month_spend_usd: string;
  /** Platform-key runs this month the price table could not price. */
  month_unpriced: { runs: number; tokens: number; models: string[] };
  platform_key_available: boolean;
  platform_model: string;
  can_edit: boolean;
}

export interface AIConfigUpdate {
  ai_enabled?: AIEnabled;
  ai_credential?: number | null;
  ai_allow_platform_key?: boolean | null;
  ai_model?: string;
  ai_monthly_quota_usd?: string | null;
  ai_temperature?: number | null;
}

export interface AIPlatformProvider {
  provider: string;
  label: string;
  configured: boolean;
  last4: string;
}

export interface AIPlatform {
  ai_enabled_default: boolean;
  ai_default_model: string;
  ai_default_quota_usd: string;
  providers: AIPlatformProvider[];
  model_provider: string;
  has_key_for_model: boolean;
}

export interface AIPlatformUpdate {
  ai_enabled_default?: boolean | null;
  ai_default_model?: string;
  ai_default_quota_usd?: string | null;
  /** provider -> key. A key replaces; "" clears; absent is untouched. */
  keys?: Record<string, string>;
}

export interface AIModel {
  id: string;
  label: string;
  /** Derived from the model id by the server; never asked of the reader. */
  provider: string;
  provider_label: string;
}

/** A provider whose catalogue could not be read, named rather than dropped. */
export interface AIProviderError {
  provider: string;
  label: string;
  reason: string;
}

export interface AIModelCatalogue {
  models: AIModel[];
  unavailable: AIProviderError[];
}

export interface AICredential {
  id: number;
  nickname: string;
  provider: string;
  organization: string;
  organization_name: string;
  allow_inference: boolean;
}

export interface TeamPayload {
  organization: string;
  name: string;
  description?: string;
  lead?: string | null;
  is_active?: boolean;
}

export interface DeskComment {
  id: string;
  body: string;
  author: number | null;
  author_email: string | null;
  client_visible: boolean;
  created: string;
}

/**
 * What a person may edit on a ticket directly. Mirrors the server's
 * `EDITABLE_FIELDS`; anything else goes through its own endpoint — a status
 * through `transition`, an assignee through `assign`, an owner through
 * `transfer`.
 */
export type DeskTicketPatch = Partial<
  Pick<
    DeskTicketDetail,
    | "title"
    | "detail"
    | "recommendation"
    | "notes"
    | "category"
    | "priority"
    | "risk_level"
    | "reversible"
    | "automation_eligible"
    | "estimated_effort_hours"
    | "actual_effort_hours"
    | "rate_snapshot"
    | "rate_currency_snapshot"
    | "remediation_requested"
    | "remediation_urgency"
    | "remediation_notes"
    | "owner_type"
  >
>;

// --- calls ------------------------------------------------------------------

const BASE = "/desk";

/** The query parameter a per-organization config call carries, or none. */
function configTarget(
  organization?: string | null,
): { organization: string } | undefined {
  return organization ? { organization } : undefined;
}

export const deskApi = {
  listTickets: async (
    filters: DeskTicketFilters = {},
  ): Promise<DeskTicketPage> => {
    const { data } = await apiClient.get<DeskTicketPage>(`${BASE}/tickets/`, {
      params: filters,
    });
    return data;
  },

  getTicket: async (id: string): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.get<DeskTicketDetail>(
      `${BASE}/tickets/${id}/`,
    );
    return data;
  },

  getEvidence: async (id: string): Promise<EvidenceRecord[]> => {
    const { data } = await apiClient.get<EvidenceRecord[]>(
      `${BASE}/tickets/${id}/evidence/`,
    );
    return data;
  },

  getHistory: async (id: string): Promise<DeskHistory> => {
    const { data } = await apiClient.get<DeskHistory>(
      `${BASE}/tickets/${id}/history/`,
    );
    return data;
  },

  listComments: async (id: string): Promise<DeskComment[]> => {
    const { data } = await apiClient.get<DeskComment[]>(
      `${BASE}/tickets/${id}/comments/`,
    );
    return data;
  },

  /**
   * A comment is internal unless said otherwise. `client_visible` is the
   * Jira Service Desk split between an internal note and a reply the
   * customer sees; the default stays closed because a comment that reaches
   * the client by omission is not recoverable.
   */
  addComment: async (
    id: string,
    body: string,
    clientVisible = false,
  ): Promise<DeskComment> => {
    const { data } = await apiClient.post<DeskComment>(
      `${BASE}/tickets/${id}/comments/`,
      { body, client_visible: clientVisible },
    );
    return data;
  },

  /**
   * A transition is its own endpoint, never a PATCH on `status`: it has entry
   * gates, side effects and its own permission, and the required fields depend
   * on where the ticket is going.
   */
  transition: async (
    id: string,
    payload: { status: DeskStatus; reason?: string } & Record<string, unknown>,
  ): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.post<DeskTicketDetail>(
      `${BASE}/tickets/${id}/transition/`,
      payload,
    );
    return data;
  },

  transferTargets: async (
    id: string,
  ): Promise<Array<{ id: string; name: string; type: string }>> => {
    const { data } = await apiClient.get(
      `${BASE}/tickets/${id}/transfer-targets/`,
    );
    return data;
  },

  transfer: async (
    id: string,
    payload: { to_org: string; reason: string },
  ): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.post<DeskTicketDetail>(
      `${BASE}/tickets/${id}/transfer/`,
      payload,
    );
    return data;
  },

  assign: async (
    id: string,
    payload: { team?: string | null; user?: string | null },
  ): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.post<DeskTicketDetail>(
      `${BASE}/tickets/${id}/assign/`,
      payload,
    );
    return data;
  },

  /**
   * Who may be put on this ticket — members of the organization that owes the
   * work, and nobody else. Server-computed: offering anyone else would let an
   * assignment move responsibility across an organization boundary without the
   * assignment row that makes a transfer auditable.
   */
  assignableUsers: async (id: string): Promise<AssignableUser[]> => {
    const { data } = await apiClient.get<AssignableUser[]>(
      `${BASE}/tickets/${id}/assignable-users/`,
    );
    return data;
  },

  /**
   * Raise a ticket by hand.
   *
   * A queue that cannot hold human work is a queue people work around: the real
   * list then lives in a spreadsheet and the realised-savings record is
   * incomplete in a way nobody can see. The server stamps the source and the
   * owner class, so neither is sent.
   */
  createTicket: async (
    payload: RaiseItemPayload,
  ): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.post<DeskTicketDetail>(
      `${BASE}/tickets/`,
      payload,
    );
    return data;
  },

  /** Edit the fields a person may edit directly. Audited server-side. */
  updateTicket: async (
    id: string,
    patch: DeskTicketPatch,
  ): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.patch<DeskTicketDetail>(
      `${BASE}/tickets/${id}/`,
      patch,
    );
    return data;
  },

  queueSummary: async (currency?: string): Promise<QueueSummary> => {
    const { data } = await apiClient.get<QueueSummary>(
      `${BASE}/analytics/queue/`,
      { params: currency ? { currency } : undefined },
    );
    return data;
  },

  bySource: async (currency?: string): Promise<SourceScorecard[]> => {
    const { data } = await apiClient.get<SourceScorecard[]>(
      `${BASE}/analytics/by-source/`,
      { params: currency ? { currency } : undefined },
    );
    return data;
  },

  /**
   * The queue rolled up by the level directly below the viewer — MSPs for a
   * distributor, companies for an MSP, one row for a company.
   *
   * No level parameter, deliberately: the server derives it from the resolved
   * scope. Letting the client name a level would let a company ask for the
   * distributor rollup, and the answer to that is another tenant's data.
   */
  byChildOrg: async (currency?: string): Promise<ChildOrgRollup[]> => {
    const { data } = await apiClient.get<ChildOrgRollup[]>(
      `${BASE}/analytics/by-child-org/`,
      { params: currency ? { currency } : undefined },
    );
    return data;
  },

  realised: async (currency?: string): Promise<RealisedSummary> => {
    const { data } = await apiClient.get<RealisedSummary>(
      `${BASE}/analytics/realised/`,
      { params: currency ? { currency } : undefined },
    );
    return data;
  },

  listSources: async (): Promise<DeskSource[]> => {
    const { data } = await apiClient.get<DeskSource[]>(`${BASE}/sources/`);
    return data;
  },

  listTeams: async (): Promise<{ results: DeskTeam[] }> => {
    const { data } = await apiClient.get(`${BASE}/teams/`);
    return data;
  },

  listRuns: async (): Promise<{ results: DeskRun[] }> => {
    const { data } = await apiClient.get(`${BASE}/runs/`);
    return data;
  },

  me: async (): Promise<DeskMe> => {
    const { data } = await apiClient.get<DeskMe>(`${BASE}/me/`);
    return data;
  },
  permissionsCatalog: async (): Promise<DeskPermissionsCatalog> => {
    const { data } = await apiClient.get<DeskPermissionsCatalog>(
      `${BASE}/config/permissions/`,
    );
    return data;
  },

  getRun: async (id: string): Promise<DeskRun> => {
    const { data } = await apiClient.get<DeskRun>(`${BASE}/runs/${id}/`);
    return data;
  },

  runNow: async (payload: RunNowPayload): Promise<RunDispatch> => {
    const { data } = await apiClient.post<RunDispatch>(
      `${BASE}/runs/`,
      payload,
    );
    return data;
  },

  cancelRun: async (id: string): Promise<DeskRun> => {
    const { data } = await apiClient.post<DeskRun>(
      `${BASE}/runs/${id}/cancel/`,
    );
    return data;
  },

  sourceReadiness: async (key: string): Promise<SourceReadiness[]> => {
    const { data } = await apiClient.get<SourceReadiness[]>(
      `${BASE}/sources/${key}/readiness/`,
    );
    return data;
  },

  handBack: async (id: string, reason: string): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.post<DeskTicketDetail>(
      `${BASE}/tickets/${id}/hand-back/`,
      { reason },
    );
    return data;
  },

  createTeam: async (payload: TeamPayload): Promise<DeskTeam> => {
    const { data } = await apiClient.post<DeskTeam>(`${BASE}/teams/`, payload);
    return data;
  },

  updateTeam: async (
    id: string,
    payload: Partial<TeamPayload>,
  ): Promise<DeskTeam> => {
    const { data } = await apiClient.patch<DeskTeam>(
      `${BASE}/teams/${id}/`,
      payload,
    );
    return data;
  },

  addTeamMember: async (id: string, user: string): Promise<DeskTeam> => {
    const { data } = await apiClient.post<DeskTeam>(
      `${BASE}/teams/${id}/members/`,
      { user },
    );
    return data;
  },

  removeTeamMember: async (id: string, user: string): Promise<DeskTeam> => {
    const { data } = await apiClient.delete<DeskTeam>(
      `${BASE}/teams/${id}/members/`,
      {
        data: { user },
      },
    );
    return data;
  },

  throughput: async (
    period: "week" | "month" = "week",
  ): Promise<ThroughputRow[]> => {
    const { data } = await apiClient.get<ThroughputRow[]>(
      `${BASE}/analytics/throughput/`,
      {
        params: { period },
      },
    );
    return data;
  },

  /**
   * Per-organization configuration names its target explicitly.
   *
   * `organization` is the id of the organization the call is about; omitted,
   * the server uses the caller's own (platform staff, who have none, read the
   * platform's defaults and are refused on a write). It used to be read from
   * the scope bar, which made the same page configure the platform or a
   * company depending on state nothing on the page showed.
   */
  aiConfig: async (organization?: string | null): Promise<AIConfig> => {
    const { data } = await apiClient.get<AIConfig>(`${BASE}/config/ai/`, {
      params: configTarget(organization),
    });
    return data;
  },

  updateAIConfig: async (
    payload: AIConfigUpdate,
    organization?: string | null,
  ): Promise<AIConfig> => {
    const { data } = await apiClient.put<AIConfig>(
      `${BASE}/config/ai/`,
      payload,
      { params: configTarget(organization) },
    );
    return data;
  },

  aiPlatform: async (): Promise<AIPlatform> => {
    const { data } = await apiClient.get<AIPlatform>(
      `${BASE}/config/ai/platform/`,
    );
    return data;
  },

  updateAIPlatform: async (payload: AIPlatformUpdate): Promise<AIPlatform> => {
    const { data } = await apiClient.put<AIPlatform>(
      `${BASE}/config/ai/platform/`,
      payload,
    );
    return data;
  },

  /**
   * Every model in reach, across every configured key, in one call.
   *
   * `provider` narrows it, but nothing in the interface asks for one: the
   * provider is read off the model id, so making someone choose it first would
   * be asking a question whose answer is already on screen.
   */
  aiModels: async (
    provider?: string,
    organization?: string | null,
  ): Promise<AIModelCatalogue> => {
    const { data } = await apiClient.get<AIModelCatalogue>(
      `${BASE}/config/ai/models/`,
      {
        params: {
          ...(provider ? { provider } : {}),
          ...configTarget(organization),
        },
      },
    );
    return data;
  },

  aiCredentials: async (
    organization?: string | null,
  ): Promise<AICredential[]> => {
    const { data } = await apiClient.get<AICredential[]>(
      `${BASE}/config/ai/credentials/`,
      { params: configTarget(organization) },
    );
    return data;
  },

  byOwnerClass: async (currency?: string): Promise<OwnerClassRow[]> => {
    const { data } = await apiClient.get<OwnerClassRow[]>(
      `${BASE}/analytics/by-owner-class/`,
      { params: currency ? { currency } : undefined },
    );
    return data;
  },

  // --- watchers -------------------------------------------------------------
  listWatchers: async (id: string): Promise<DeskWatcher[]> => {
    const { data } = await apiClient.get<DeskWatcher[]>(
      `${BASE}/tickets/${id}/watchers/`,
    );
    return data;
  },
  /** No body: the caller follows the ticket. `{user}` or `{team}` puts someone else on it. */
  watch: async (
    id: string,
    who: { user?: string; team?: string } = {},
  ): Promise<DeskWatcher> => {
    const { data } = await apiClient.post<DeskWatcher>(
      `${BASE}/tickets/${id}/watchers/`,
      who,
    );
    return data;
  },
  unwatch: async (id: string, watcherId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/tickets/${id}/watchers/${watcherId}/`);
  },

  // --- campaigns --------------------------------------------------------------
  listCampaigns: async (
    params: { is_active?: boolean; organization?: string; q?: string } = {},
  ): Promise<{ results: DeskCampaign[]; count: number }> => {
    const { data } = await apiClient.get<{
      results: DeskCampaign[];
      count: number;
    }>(`${BASE}/campaigns/`, { params });
    return data;
  },
  getCampaign: async (id: string): Promise<DeskCampaign> => {
    const { data } = await apiClient.get<DeskCampaign>(
      `${BASE}/campaigns/${id}/`,
    );
    return data;
  },
  createCampaign: async (payload: CampaignPayload): Promise<DeskCampaign> => {
    const { data } = await apiClient.post<DeskCampaign>(
      `${BASE}/campaigns/`,
      payload,
    );
    return data;
  },
  updateCampaign: async (
    id: string,
    payload: Partial<Pick<CampaignPayload, "name" | "rule_id" | "is_active">>,
  ): Promise<DeskCampaign> => {
    const { data } = await apiClient.patch<DeskCampaign>(
      `${BASE}/campaigns/${id}/`,
      payload,
    );
    return data;
  },
  /** Null detaches. */
  setCampaign: async (
    id: string,
    campaign: string | null,
  ): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.post<DeskTicketDetail>(
      `${BASE}/tickets/${id}/campaign/`,
      { campaign },
    );
    return data;
  },

  // --- merge and bulk ---------------------------------------------------------
  /** Folds `id` into `into`; `id` becomes superseded and points at `into`. */
  merge: async (
    id: string,
    into: string,
    reason: string,
  ): Promise<DeskTicketDetail> => {
    const { data } = await apiClient.post<DeskTicketDetail>(
      `${BASE}/tickets/${id}/merge/`,
      { into, reason },
    );
    return data;
  },
  bulk: async (payload: BulkRequest): Promise<BulkResponse> => {
    const { data } = await apiClient.post<BulkResponse>(
      `${BASE}/tickets/bulk/`,
      payload,
    );
    return data;
  },

  // --- SLA ----------------------------------------------------------------------
  slaSummary: async (): Promise<SlaSummary> => {
    const { data } = await apiClient.get<SlaSummary>(`${BASE}/analytics/sla/`);
    return data;
  },
  slaConfig: async (organization?: string | null): Promise<SlaConfig> => {
    const { data } = await apiClient.get<SlaConfig>(`${BASE}/config/sla/`, {
      params: configTarget(organization),
    });
    return data;
  },
  updateSlaConfig: async (
    payload: SlaConfigUpdate,
    organization?: string | null,
  ): Promise<SlaConfig> => {
    const { data } = await apiClient.put<SlaConfig>(
      `${BASE}/config/sla/`,
      payload,
      {
        params: configTarget(organization),
      },
    );
    return data;
  },

  // --- schedule -----------------------------------------------------------------
  scheduleConfig: async (
    organization?: string | null,
  ): Promise<ScheduleConfig> => {
    const { data } = await apiClient.get<ScheduleConfig>(
      `${BASE}/config/schedule/`,
      { params: configTarget(organization) },
    );
    return data;
  },
  /** Pause or resume one source's scheduled runs for `organization` and everything under it. */
  updateSchedule: async (
    payload: ScheduleUpdate,
    organization?: string | null,
  ): Promise<ScheduleConfig> => {
    const { data } = await apiClient.put<ScheduleConfig>(
      `${BASE}/config/schedule/`,
      payload,
      { params: configTarget(organization) },
    );
    return data;
  },

  // --- labour -------------------------------------------------------------------
  labourConfig: async (organization?: string | null): Promise<LabourConfig> => {
    const { data } = await apiClient.get<LabourConfig>(
      `${BASE}/config/labour/`,
      {
        params: configTarget(organization),
      },
    );
    return data;
  },
  updateLabourConfig: async (
    payload: LabourConfigUpdate,
    organization?: string | null,
  ): Promise<LabourConfig> => {
    const { data } = await apiClient.put<LabourConfig>(
      `${BASE}/config/labour/`,
      payload,
      { params: configTarget(organization) },
    );
    return data;
  },
};
